import React, { useEffect, useState } from "react";
import BTable from "react-bootstrap/Table";
import {
  useTable,
  useSortBy,
  usePagination,
  useGlobalFilter,
} from "react-table";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import "../../src/css/reusableTable.css";

export function Table({
  columns,
  data,
  setPageno,
  pagecount,
  show,
  pageno,
  color = "#3a2e28",
}) {
  const [pagenumber, setPagenumber] = useState(1);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: pageno ? pageno - 1 : 0, pageSize: 10 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  useEffect(() => {
    setPagenumber(pageIndex + 1);
  }, [pageIndex]);

  useEffect(() => {
    gotoPage(0);
  }, [data]);

  const totalPages = Math.ceil((data?.length || 0) / 10);
  const isServerPaginated = Boolean(pagecount);

  const safeSetPageno = (next) => {
    if (typeof setPageno === "function") {
      setPageno(next);
    } else {
      console.warn(
        "Table: pagecount was provided but setPageno was not. Pass a setPageno handler."
      );
    }
  };

  const currentPageNum = isServerPaginated ? (pagecount?.current_page || 1) : (pageIndex + 1);
  const lastPageNum = isServerPaginated ? (pagecount?.last_page || 1) : (pageCount || totalPages || 1);

  // Generate clean page number list e.g. [1, 2, 3, 4, 5] or [1, '...', 4, 5, 6, '...', 10]
  const getPageNumbers = () => {
    const pages = [];
    if (lastPageNum <= 7) {
      for (let i = 1; i <= lastPageNum; i++) pages.push(i);
    } else {
      if (currentPageNum <= 4) {
        pages.push(1, 2, 3, 4, 5, '...', lastPageNum);
      } else if (currentPageNum >= lastPageNum - 3) {
        pages.push(1, '...', lastPageNum - 4, lastPageNum - 3, lastPageNum - 2, lastPageNum - 1, lastPageNum);
      } else {
        pages.push(1, '...', currentPageNum - 1, currentPageNum, currentPageNum + 1, '...', lastPageNum);
      }
    }
    return pages;
  };

  return (
    <div className="rt-wrapper">
      <div className="rt-table-scroll">
        <BTable responsive {...getTableProps()} className="rt-table">
          <thead className="rt-thead">
            {headerGroups.map((headerGroup, ind) => (
              <tr className="rt-header-row" {...headerGroup.getHeaderGroupProps()} key={ind}>
                {headerGroup.headers.map((column, indx) => (
                  <th
                    className="rt-th"
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    key={indx}
                  >
                    <span className="rt-th-label">{column.render("Header")}</span>
                    {column.isSorted && (
                      <span className={`rt-sort-icon ${column.isSortedDesc ? "desc" : "asc"}`} />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="rt-empty">
                  No data available
                </td>
              </tr>
            ) : (
              page.map((row, i) => {
                prepareRow(row);
                return (
                  <tr className="rt-row" {...row.getRowProps()} key={i}>
                    {row.cells.map((cell, ind) => (
                      <td
                        className="rt-cell"
                        style={{ color }}
                        {...cell.getCellProps()}
                        key={ind}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </BTable>
      </div>

      {show && (lastPageNum > 1 || isServerPaginated) && (
        <div className="rt-pagination-bar rt-pagination-centered">
          <div className="rt-pagination-controls">
            {/* First Page button */}
            <button
              type="button"
              className={`rt-page-btn rt-arrow-btn ${currentPageNum <= 1 ? "disabled" : ""}`}
              onClick={() => {
                if (currentPageNum <= 1) return;
                if (isServerPaginated) safeSetPageno(1);
                else {
                  gotoPage(0);
                  if (typeof setPageno === "function") setPageno(1);
                }
              }}
              disabled={currentPageNum <= 1}
              title="First Page"
            >
              <ChevronsLeft size={16} />
            </button>

            {/* Prev Page button */}
            <button
              type="button"
              className={`rt-page-btn rt-arrow-btn ${currentPageNum <= 1 ? "disabled" : ""}`}
              onClick={() => {
                if (currentPageNum <= 1) return;
                if (isServerPaginated) safeSetPageno(currentPageNum - 1);
                else {
                  previousPage();
                  if (typeof setPageno === "function") setPageno(pageno - 1);
                }
              }}
              disabled={currentPageNum <= 1}
              title="Previous Page"
            >
              <ChevronLeft size={16} />
            </button>

            {/* Page number pills */}
            {getPageNumbers().map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} className="rt-page-ellipsis">
                  ...
                </span>
              ) : (
                <button
                  type="button"
                  key={p}
                  className={`rt-page-btn ${currentPageNum === p ? "active" : ""}`}
                  onClick={() => {
                    if (currentPageNum === p) return;
                    if (isServerPaginated) safeSetPageno(p);
                    else {
                      gotoPage(p - 1);
                      if (typeof setPageno === "function") setPageno(p);
                    }
                  }}
                >
                  {p}
                </button>
              )
            )}

            {/* Next Page button */}
            <button
              type="button"
              className={`rt-page-btn rt-arrow-btn ${currentPageNum >= lastPageNum ? "disabled" : ""}`}
              onClick={() => {
                if (currentPageNum >= lastPageNum) return;
                if (isServerPaginated) safeSetPageno(currentPageNum + 1);
                else {
                  nextPage();
                  if (typeof setPageno === "function") setPageno(pageno + 1);
                }
              }}
              disabled={currentPageNum >= lastPageNum}
              title="Next Page"
            >
              <ChevronRight size={16} />
            </button>

            {/* Last Page button */}
            <button
              type="button"
              className={`rt-page-btn rt-arrow-btn ${currentPageNum >= lastPageNum ? "disabled" : ""}`}
              onClick={() => {
                if (currentPageNum >= lastPageNum) return;
                if (isServerPaginated) safeSetPageno(lastPageNum);
                else {
                  gotoPage(lastPageNum - 1);
                  if (typeof setPageno === "function") setPageno(lastPageNum);
                }
              }}
              disabled={currentPageNum >= lastPageNum}
              title="Last Page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
